import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, Users, Clock, Trophy, TrendingUp, Shuffle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './App.css';

// Player data - 23 players
const PLAYER_DATA = [
  { name: 'Alex', rating: 3.7, gender: 'M' },
  { name: 'Betty', rating: 3.37, gender: 'F' },
  { name: 'Charles', rating: 3.35, gender: 'M' },
  { name: 'Denise', rating: 3.53, gender: 'F' },
  { name: 'Eric', rating: 3.37, gender: 'M' },
  { name: 'Fred', rating: 3.32, gender: 'M' },
  { name: 'Gemma', rating: 3.29, gender: 'F' },
  { name: 'Harry', rating: 3.32, gender: 'M' },
  { name: 'Ian', rating: 3.4, gender: 'M' },
  { name: 'Jenny', rating: 3.67, gender: 'F' },
  { name: 'Ken', rating: 3.71, gender: 'M' },
  { name: 'Liam', rating: 3.44, gender: 'M' },
  { name: 'Mike', rating: 3.66, gender: 'M' },
  { name: 'Nellie', rating: 3.39, gender: 'F' },
  { name: 'Oscar', rating: 3.62, gender: 'M' },
  { name: 'Pete', rating: 3.31, gender: 'M' },
  { name: 'Queenie', rating: 3.41, gender: 'F' },
  { name: 'Randy', rating: 3.56, gender: 'M' },
  { name: 'Steve', rating: 3.52, gender: 'M' },
  { name: 'Theresa', rating: 3.5, gender: 'F' },
  { name: 'Umberto', rating: 3.6, gender: 'M' },
  { name: 'Viktor', rating: 3.71, gender: 'M' },
  { name: 'Walter', rating: 3.39, gender: 'M' }
];

// Create initial player state
function createPlayer(playerData, id) {
  return {
    ...playerData,
    id,
    currentRating: playerData.rating,
    gamesPlayed: 0,
    ratingHistory: [{ game: 0, rating: playerData.rating }],
    totalChange: 0,
    partnerships: new Set(),
    opponents: new Map(),
    waitTime: 0,
    lastGameEnd: 0
  };
}

// Smart Bench Manager
class SmartBenchManager {
  constructor() {
    this.sessionPartnerships = new Map();
    this.gameNumber = 1;
  }

  // Select next 4 players from bench
  selectNext4(benchPlayers, useSmartRotation) {
    if (benchPlayers.length < 4) {
      console.error("Not enough bench players:", benchPlayers.length);
      return benchPlayers;
    }

    if (!useSmartRotation) {
      // Linear: always take first 4
      return benchPlayers.slice(0, 4);
    }

    // Smart: Consider wait times and game balance
    const candidates = [];
    
    // Candidate 1: Top 4 by wait time
    const byWaitTime = [...benchPlayers].sort((a, b) => (b.waitTime || 0) - (a.waitTime || 0));
    candidates.push(byWaitTime.slice(0, 4));
    
    // Candidate 2: Balanced by games played
    const byGames = [...benchPlayers].sort((a, b) => a.gamesPlayed - b.gamesPlayed);
    candidates.push(byGames.slice(0, 4));
    
    // Candidate 3: Mixed positions to avoid clustering
    if (benchPlayers.length >= 6) {
      const mixed = [benchPlayers[0], benchPlayers[1], benchPlayers[3], benchPlayers[5]];
      candidates.push(mixed);
    } else {
      candidates.push(benchPlayers.slice(0, 4));
    }

    // Score candidates
    const scored = candidates.map(combo => ({
      players: combo,
      score: this.scoreCombination(combo)
    }));

    scored.sort((a, b) => b.score - a.score);
    return scored[0].players;
  }

  scoreCombination(fourPlayers) {
    let score = 0;

    // Factor 1: Wait time balance (higher average wait = better)
    const avgWait = fourPlayers.reduce((sum, p) => sum + (p.waitTime || 0), 0) / 4;
    score += avgWait * 20;

    // Factor 2: Games played balance (prefer even distribution)
    const gamesCounts = fourPlayers.map(p => p.gamesPlayed);
    const maxGames = Math.max(...gamesCounts);
    const minGames = Math.min(...gamesCounts);
    score += (3 - (maxGames - minGames)) * 15;

    // Factor 3: Partnership novelty
    const partnerships = [
      [fourPlayers[0].id, fourPlayers[1].id].sort().join('-'),
      [fourPlayers[2].id, fourPlayers[3].id].sort().join('-')
    ];

    partnerships.forEach(partnership => {
      const count = this.sessionPartnerships.get(partnership) || 0;
      score += Math.max(0, 25 - count * 12); // Heavily penalize repeats
    });

    return score;
  }

  trackPartnership(p1Id, p2Id) {
    const key = [p1Id, p2Id].sort().join('-');
    this.sessionPartnerships.set(key, (this.sessionPartnerships.get(key) || 0) + 1);
  }

  // Insert exiting players into bench
  insertIntoBench(currentBench, exitingPlayers, useSmartRotation) {
    let newBench = [...currentBench];

    exitingPlayers.forEach(player => {
      // Reset player state for bench
      player.waitTime = 0; // Will be incremented next round
      
      if (useSmartRotation) {
        // Smart: Insert towards middle/back to ensure they wait
        const insertPos = Math.max(6, newBench.length - Math.floor(Math.random() * 3));
        newBench.splice(Math.min(insertPos, newBench.length), 0, player);
      } else {
        // Linear: Add to end
        newBench.push(player);
      }
    });

    return newBench;
  }
}

// DINKR Rating System
function calculateDINKR(team1Ratings, team2Ratings, actualScores, winningTeam) {
  const intensity = 0.008;
  const Sx = 0.15;
  
  const team1Combined = team1Ratings[0] + team1Ratings[1];
  const team2Combined = team2Ratings[0] + team2Ratings[1];
  
  const ratingDiff = Math.abs(team1Combined - team2Combined);
  const loserPredicted = Math.max(0, Math.round(11 - (ratingDiff / Sx)));
  
  let PS_1, PS_2;
  if (team1Combined > team2Combined) {
    PS_1 = 11; PS_2 = loserPredicted;
  } else if (team2Combined > team1Combined) {
    PS_1 = loserPredicted; PS_2 = 11;
  } else {
    PS_1 = 11; PS_2 = 10;
  }
  
  const losingTeam = winningTeam === 1 ? 2 : 1;
  const predictedLoserScore = losingTeam === 1 ? PS_1 : PS_2;
  const actualLoserScore = losingTeam === 1 ? actualScores.team1 : actualScores.team2;
  
  const raw_delta = actualLoserScore - predictedLoserScore;
  const delta_s = Math.abs(raw_delta) <= 1 ? 0 : raw_delta;
  
  let RA = Math.abs(delta_s) * intensity;
  
  const predictedWinner = team1Combined > team2Combined ? 1 : 
                         team2Combined > team1Combined ? 2 : 0;
  if (predictedWinner && predictedWinner !== winningTeam && ratingDiff >= 0.5) {
    RA *= 1.5;
  }
  
  if (RA === 0) RA = intensity * 0.5;
  
  let newRatings = [...team1Ratings, ...team2Ratings];
  
  if (delta_s > 0) {
    if (losingTeam === 1) {
      newRatings[0] += RA; newRatings[1] += RA;
      newRatings[2] -= RA; newRatings[3] -= RA;
    } else {
      newRatings[0] -= RA; newRatings[1] -= RA;
      newRatings[2] += RA; newRatings[3] += RA;
    }
  } else if (delta_s < 0) {
    if (losingTeam === 1) {
      newRatings[0] -= RA; newRatings[1] -= RA;
      newRatings[2] += RA; newRatings[3] += RA;
    } else {
      newRatings[0] += RA; newRatings[1] += RA;
      newRatings[2] -= RA; newRatings[3] -= RA;
    }
  } else {
    if (winningTeam === 1) {
      newRatings[0] += RA; newRatings[1] += RA;
      newRatings[2] -= RA; newRatings[3] -= RA;
    } else {
      newRatings[0] -= RA; newRatings[1] -= RA;
      newRatings[2] += RA; newRatings[3] += RA;
    }
  }
  
  return { newRatings, adjustment: RA };
}

function generateScore() {
  const scoreType = Math.random();
  let team1Score, team2Score, winner;
  
  if (scoreType < 0.1) {
    const base = 9 + Math.floor(Math.random() * 2);
    team1Score = base + (Math.random() < 0.5 ? 1 : 0);
    team2Score = base + (team1Score === base ? 1 : 0);
    winner = team1Score > team2Score ? 1 : 2;
  } else {
    const loserScore = Math.floor(Math.random() * 11);
    if (Math.random() < 0.5) {
      team1Score = 11; team2Score = loserScore; winner = 1;
    } else {
      team1Score = loserScore; team2Score = 11; winner = 2;
    }
  }
  
  return { team1: team1Score, team2: team2Score, winner };
}

function PickleballSimulation() {
  // Initialize all 23 players
  const [allPlayers, setAllPlayers] = useState(() => 
    PLAYER_DATA.map((p, i) => createPlayer(p, i))
  );

  // Explicit court tracking
  const [court1, setCourt1] = useState(() => allPlayers.slice(0, 4));
  const [court2, setCourt2] = useState(() => allPlayers.slice(4, 8));
  const [court3, setCourt3] = useState(() => allPlayers.slice(8, 12));
  
  // Bench queue
  const [bench, setBench] = useState(() => allPlayers.slice(12));

  // Game tracking
  const [court1Games, setCourt1Games] = useState(0);
  const [court2Games, setCourt2Games] = useState(0);
  const [court3Games, setCourt3Games] = useState(0);
  
  const [gameNumber, setGameNumber] = useState(1);
  const [rotationManager] = useState(() => new SmartBenchManager());
  const [log, setLog] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [useSmartRotation, setUseSmartRotation] = useState(true);

  const totalGamesCompleted = court1Games + court2Games + court3Games;

  const simulateNextGame = useCallback(() => {
    if (totalGamesCompleted >= 27) {
      setLog(prev => [...prev, "🏁 Session complete! 27 games finished."]);
      setIsRunning(false);
      return;
    }

    // Find which court should play next (simple round-robin to ensure even distribution)
    let selectedCourtId;
    let selectedCourtPlayers;
    let setSelectedCourt;
    
    if (court1Games <= court2Games && court1Games <= court3Games && court1Games < 9) {
      selectedCourtId = 1;
      selectedCourtPlayers = court1;
      setSelectedCourt = setCourt1;
    } else if (court2Games <= court3Games && court2Games < 9) {
      selectedCourtId = 2;
      selectedCourtPlayers = court2;
      setSelectedCourt = setCourt2;
    } else if (court3Games < 9) {
      selectedCourtId = 3;
      selectedCourtPlayers = court3;
      setSelectedCourt = setCourt3;
    } else {
      setIsRunning(false);
      return;
    }

    if (bench.length < 4) {
      setLog(prev => [...prev, `⚠️ Not enough bench players: ${bench.length}`]);
      setIsRunning(false);
      return;
    }

    // Get next 4 players from bench
    const nextPlayers = rotationManager.selectNext4(bench, useSmartRotation);
    
    if (nextPlayers.length !== 4) {
      setLog(prev => [...prev, `⚠️ Invalid selection: ${nextPlayers.length} players`]);
      setIsRunning(false);
      return;
    }

    // Simulate the game
    const team1 = [nextPlayers[0], nextPlayers[1]];
    const team2 = [nextPlayers[2], nextPlayers[3]];
    const score = generateScore();

    // Calculate DINKR changes
    const team1Ratings = [team1[0].currentRating, team1[1].currentRating];
    const team2Ratings = [team2[0].currentRating, team2[1].currentRating];
    const dinkr = calculateDINKR(team1Ratings, team2Ratings, score, score.winner);

    // Update player stats WITH PARTNERSHIPS
    const updatedGamePlayers = nextPlayers.map((player, index) => {
      const newRating = dinkr.newRatings[index];
      const change = newRating - player.currentRating;
      
      // Determine this player's partner
      let partnerId;
      if (index === 0) partnerId = nextPlayers[1].id;  // Team 1
      else if (index === 1) partnerId = nextPlayers[0].id;
      else if (index === 2) partnerId = nextPlayers[3].id;  // Team 2
      else partnerId = nextPlayers[2].id;
      
      return {
        ...player,
        currentRating: newRating,
        gamesPlayed: player.gamesPlayed + 1,
        lastGameEnd: gameNumber,
        totalChange: player.totalChange + change,
        ratingHistory: [...player.ratingHistory, { game: gameNumber, rating: newRating }],
        waitTime: 0,
        partnerships: new Set([...player.partnerships, partnerId])
      };
    });

    // Track partnerships in session manager
    rotationManager.trackPartnership(team1[0].id, team1[1].id);
    rotationManager.trackPartnership(team2[0].id, team2[1].id);

    // Update the selected court with new players
    setSelectedCourt(updatedGamePlayers);
    
    // Update bench with proper wait time handling
    setBench(currentBench => {
      // Remove selected players from bench
      const remainingBench = currentBench.filter(p => !nextPlayers.some(np => np.id === p.id));
      
      // INCREMENT WAIT TIME for all remaining bench players
      const benchWithIncrementedWait = remainingBench.map(player => ({
        ...player,
        waitTime: (player.waitTime || 0) + 1
      }));
      
      // Add exiting players to bench (with waitTime = 0)
      const exitingPlayersForBench = selectedCourtPlayers.map(player => ({
        ...player,
        waitTime: 0
      }));
      
      // Use smart/linear insertion logic
      let newBench = [...benchWithIncrementedWait];
      
      exitingPlayersForBench.forEach(player => {
        if (useSmartRotation) {
          const insertPos = Math.max(6, newBench.length - Math.floor(Math.random() * 3));
          newBench.splice(Math.min(insertPos, newBench.length), 0, player);
        } else {
          newBench.push(player);
        }
      });
      
      return newBench;
    });

    // Update court game count
    if (selectedCourtId === 1) setCourt1Games(prev => prev + 1);
    else if (selectedCourtId === 2) setCourt2Games(prev => prev + 1);
    else setCourt3Games(prev => prev + 1);

    // Update allPlayers with both court players AND bench wait time changes
    setAllPlayers(prev => prev.map(player => {
      // Check if this player was just updated (in the game)
      const updatedPlayer = updatedGamePlayers.find(up => up.id === player.id);
      if (updatedPlayer) {
        return updatedPlayer;
      }
      
      // Check if this player is on bench and should have incremented wait time
      const isBenchPlayer = bench.some(bp => bp.id === player.id) && 
                           !nextPlayers.some(np => np.id === player.id) &&
                           !selectedCourtPlayers.some(cp => cp.id === player.id);
      
      if (isBenchPlayer) {
        return {
          ...player,
          waitTime: (player.waitTime || 0) + 1
        };
      }
      
      // Check if this player was just moved to bench (exiting court)
      const isExitingPlayer = selectedCourtPlayers.some(cp => cp.id === player.id);
      if (isExitingPlayer) {
        return {
          ...player,
          waitTime: 0
        };
      }
      
      return player;
    }));

    // Log the game
    const ratingChanges = updatedGamePlayers.map((player, index) => {
      const change = dinkr.newRatings[index] - nextPlayers[index].currentRating;
      return `${player.name}: ${change >= 0 ? '+' : ''}${change.toFixed(3)}`;
    });

    const avgWait = nextPlayers.reduce((sum, p) => sum + (p.waitTime || 0), 0) / 4;
    const selectionMethod = useSmartRotation ? 
      `Smart (avg wait: ${avgWait.toFixed(1)}g)` : 
      'Linear';

    const logEntry = `Game ${gameNumber}: Court ${selectedCourtId} (${score.team1}-${score.team2}, Team ${score.winner} wins). ${selectionMethod}. Selected: ${nextPlayers.map(p => p.name).join(', ')}. Ratings: ${ratingChanges.join(', ')}`;
    setLog(prev => [...prev, logEntry]);

    setGameNumber(prev => prev + 1);

  }, [court1, court2, court3, court1Games, court2Games, court3Games, bench, gameNumber, totalGamesCompleted, useSmartRotation, rotationManager]);

  // Auto-step simulation
  useEffect(() => {
    if (isRunning) {
      const timer = setTimeout(() => {
        simulateNextGame();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isRunning, simulateNextGame]);

  const resetSimulation = () => {
    const initialPlayers = PLAYER_DATA.map((p, i) => createPlayer(p, i));
    setAllPlayers(initialPlayers);
    setCourt1(initialPlayers.slice(0, 4));
    setCourt2(initialPlayers.slice(4, 8));
    setCourt3(initialPlayers.slice(8, 12));
    setBench(initialPlayers.slice(12));
    setCourt1Games(0);
    setCourt2Games(0);
    setCourt3Games(0);
    setGameNumber(1);
    rotationManager.sessionPartnerships.clear();
    rotationManager.gameNumber = 1;
    setLog([]);
    setIsRunning(false);
  };

  const toggleSimulation = () => {
    if (isRunning) {
      setIsRunning(false);
    } else {
      setIsRunning(true);
      if (log.length === 0) {
        setLog([`🏓 Session started! Using ${useSmartRotation ? 'SMART' : 'LINEAR'} rotation system...`]);
      }
    }
  };

  // Calculate statistics
  const avgGamesPerPlayer = allPlayers.reduce((sum, p) => sum + p.gamesPlayed, 0) / allPlayers.length;
  const uniquePartnerships = rotationManager.sessionPartnerships.size;
  const avgWaitTime = bench.reduce((sum, p) => sum + (p.waitTime || 0), 0) / Math.max(bench.length, 1);
  
  // Chart data
  const chartData = [];
  if (totalGamesCompleted > 0) {
    const maxGames = Math.max(...allPlayers.map(p => p.ratingHistory?.length || 1));
    for (let game = 0; game < maxGames; game++) {
      const dataPoint = { game };
      allPlayers.forEach(player => {
        if (player.ratingHistory && player.ratingHistory[game]) {
          dataPoint[player.name] = player.ratingHistory[game].rating;
        }
      });
      if (Object.keys(dataPoint).length > 1) { // Ensure we have data beyond just 'game'
        chartData.push(dataPoint);
      }
    }
  }

  const colors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f',
    '#ffbb28', '#ff8042', '#8dd1e1', '#d084d0', '#87ceeb', '#dda0dd'
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-4">Pickleball Rotation Simulator</h1>
        <div className="mb-4 p-4 bg-blue-50 rounded-lg text-sm">
          <h2 className="font-semibold mb-2">Expected Results (27 games, 23 players):</h2>
          <ul className="space-y-1 text-gray-700">
            <li><strong>Games per player:</strong> 4-5 games (108 total player-games ÷ 23 = 4.7 avg)</li>
            <li><strong>Court distribution:</strong> 9 games per court</li>
            <li><strong>Wait times:</strong> 1-4g range for bench players</li>
            <li><strong>Partnership variety:</strong> Smart rotation should minimize repeats</li>
          </ul>
        </div>
        
        <div className="flex gap-4 mb-4 items-center">
          <button onClick={toggleSimulation} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isRunning ? 'Pause' : 'Start'}
          </button>
          <button onClick={simulateNextGame} disabled={isRunning || totalGamesCompleted >= 27} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50">
            Step
          </button>
          <button onClick={resetSimulation} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          
          <div className="ml-4 flex items-center gap-2">
            <Shuffle className="w-4 h-4" />
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={useSmartRotation} onChange={(e) => setUseSmartRotation(e.target.checked)} disabled={isRunning} />
              <span className="font-medium">Smart Rotation</span>
            </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Session Stats */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            Session Stats
          </h3>
          <div className="text-sm space-y-1">
            <div>Game: {gameNumber}</div>
            <div>Completed: {totalGamesCompleted}/27</div>
            <div>Avg Games/Player: {avgGamesPerPlayer.toFixed(1)}</div>
            <div>Bench Avg Wait: {avgWaitTime.toFixed(1)}g</div>
            <div className={useSmartRotation ? 'text-green-600 font-bold' : 'text-blue-600'}>
              {useSmartRotation ? 'SMART' : 'LINEAR'}
            </div>
          </div>
        </div>

        {/* Courts */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Courts
          </h3>
          <div className="text-sm space-y-2">
            <div className="text-xs">
              <div className="font-medium">Court 1 ({court1Games}/9):</div>
              <div>{court1.map(p => `${p.name}(${p.gamesPlayed}g)`).join(', ')}</div>
            </div>
            <div className="text-xs">
              <div className="font-medium">Court 2 ({court2Games}/9):</div>
              <div>{court2.map(p => `${p.name}(${p.gamesPlayed}g)`).join(', ')}</div>
            </div>
            <div className="text-xs">
              <div className="font-medium">Court 3 ({court3Games}/9):</div>
              <div>{court3.map(p => `${p.name}(${p.gamesPlayed}g)`).join(', ')}</div>
            </div>
          </div>
        </div>

        {/* Bench */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Bench Queue ({bench.length} players)
          </h3>
          <div className="text-xs space-y-1 max-h-32 overflow-y-auto">
            {bench.map((player, index) => (
              <div key={player.id} className={index < 4 ? 'bg-yellow-100 px-1 rounded font-medium' : ''}>
                {index + 1}: {player.name} (W{player.waitTime || 0}, {player.gamesPlayed}g)
              </div>
            ))}
          </div>
        </div>

        {/* Partnership Stats */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Partnership Variety
          </h3>
          <div className="text-sm space-y-1">
            <div>Unique Partnerships: {uniquePartnerships}</div>
            <div>Max Possible: 253</div>
            <div>Variety Rate: {((uniquePartnerships / 253) * 100).toFixed(1)}%</div>
            <div className="text-xs text-gray-600 mt-2">
              Repeated partnerships: {Array.from(rotationManager.sessionPartnerships.values()).filter(count => count > 1).length}
            </div>
          </div>
        </div>
      </div>

      {/* Rating Chart */}
      {totalGamesCompleted > 0 && chartData.length > 1 && (
        <div className="mt-6">
          <h3 className="text-xl font-semibold mb-4">DINKR Rating Progression</h3>
          <div className="border rounded-lg p-4 chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="game" label={{ value: 'Game Number', position: 'insideBottom', offset: -5 }} />
                <YAxis domain={['dataMin - 0.05', 'dataMax + 0.05']} label={{ value: 'Rating', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, name) => [value?.toFixed(3), name]} labelFormatter={(label) => `Game ${label}`} />
                <Legend />
                {allPlayers.filter(player => player.gamesPlayed > 0).slice(0, 10).map((player, index) => (
                  <Line key={player.id} dataKey={player.name} name={player.name} stroke={colors[index % colors.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Statistics Tables */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Player Stats */}
        <div className="border rounded-lg p-4">
          <h3 className="text-xl font-semibold mb-4">Player Statistics</h3>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="text-left p-2">Player</th>
                  <th className="text-right p-2">Games</th>
                  <th className="text-right p-2">Partners</th>
                  <th className="text-right p-2">Wait</th>
                  <th className="text-right p-2">Rating Δ</th>
                </tr>
              </thead>
              <tbody>
                {allPlayers.sort((a, b) => b.gamesPlayed - a.gamesPlayed || a.name.localeCompare(b.name)).map((player) => (
                  <tr key={player.id} className="border-t">
                    <td className="p-2 font-medium">{player.name}</td>
                    <td className="p-2 text-right">{player.gamesPlayed}</td>
                    <td className="p-2 text-right">{player.partnerships.size}</td>
                    <td className="p-2 text-right">{player.waitTime || 0}g</td>
                    <td className={`p-2 text-right font-medium ${player.totalChange > 0 ? 'text-green-600' : player.totalChange < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      {player.totalChange >= 0 ? '+' : ''}{player.totalChange.toFixed(3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Partnership Analysis */}
        <div className="border rounded-lg p-4">
          <h3 className="text-xl font-semibold mb-4">Partnership Frequency</h3>
          <div className="max-h-96 overflow-y-auto text-xs">
            {Array.from(rotationManager.sessionPartnerships.entries())
              .sort((a, b) => b[1] - a[1])
              .slice(0, 25)
              .map(([partnership, count]) => {
                const [id1, id2] = partnership.split('-').map(Number);
                const player1 = allPlayers.find(p => p.id === id1);
                const player2 = allPlayers.find(p => p.id === id2);
                return (
                  <div key={partnership} className="flex justify-between py-1 border-b border-gray-100">
                    <span>{player1?.name} & {player2?.name}</span>
                    <span className={`font-medium ${count > 2 ? 'text-red-600' : count > 1 ? 'text-orange-600' : 'text-green-600'}`}>
                      {count}x
                    </span>
                  </div>
                );
              })}
            {rotationManager.sessionPartnerships.size === 0 && (
              <div className="text-gray-500 text-center py-4">No partnerships yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Game Log */}
      <div className="mt-6">
        <h3 className="font-semibold mb-2">Game Log</h3>
        <div className="border rounded-lg p-4 h-48 overflow-y-auto bg-gray-50">
          {log.map((entry, index) => (
            <div key={index} className="text-sm mb-1 font-mono">{entry}</div>
          ))}
          {log.length === 0 && (
            <div className="text-gray-500 text-sm">Choose rotation method and click Start...</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PickleballSimulation;